import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, Pause, Square, Type, Upload, FileText, Sparkles, Check } from 'lucide-react'
import { useI18n } from '../../i18n'

/**
 * VoiceRecorder — the SHARED patient recorder surface. v242.68.0
 *
 * Extracted from DescribeVoice.jsx (v242.55.0/57.0), which was the only screen
 * carrying the current UX. FollowUpReportModal had a parallel implementation
 * that never moved past the Whisper era: a 36-bar waveform driven by
 * Math.random() (no mic input at all), a 28px red countdown, and the Whisper
 * 3-step progress panel shown even on the streaming path.
 *
 * WHY PRESENTATIONAL, AND WHY THAT MATTERS HERE:
 * every piece of state — voiceState, recordingTime, the MediaRecorder, the
 * Transcribe stream — stays in the parent. This module owns markup, the
 * AnalyserNode, and the keyframes, and nothing else. That is what makes the
 * DescribeVoice side of this change provably behaviour-neutral: there is no
 * state to move, so there is no state that can move wrong. DescribeVoice is
 * the screen App Review walks (ORELLA_RULES 1d) and it is six days from a
 * targeted review date; a logic refactor there would not have been worth it.
 *
 * ★ THE KEYFRAMES TRAVEL WITH THE COMPONENT, DELIBERATELY.
 * `orellaBreatheGreen` and `orellaSpin` were defined in a <style> block inside
 * DescribeVoice's own return. A consumer that mounts this markup without them
 * renders a static dot and a ring that does not turn — correct in review,
 * inert at runtime, which is the exact shape ORELLA_RULES 1al(b) is about.
 * <VoiceRecorderKeyframes /> is therefore not optional and not a courtesy.
 *
 * ★ LABELS FAIL OPEN. Each surface keeps its own i18n namespace
 * (`describeVoice.*` vs `followUpReport.*`) and passes it as `ns`. `t()` in
 * this app RETURNS THE KEY when a translation is missing (i18n/index.jsx:119),
 * so a missing key would render the literal string "followUpReport.listening"
 * to a patient. Every label below therefore resolves through `ts()`, which
 * falls back to English rather than to a dotted path.
 */

// Color constants — same values as both consuming screens.
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

export const formatRecordingTime = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Keyframes this module's markup depends on. Mount ONCE per screen, anywhere
 * in the tree. Duplicate @keyframes of the same name are harmless.
 */
export function VoiceRecorderKeyframes() {
  return (
    <style>{`
      @keyframes orellaBreatheGreen {
        0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(102,209,112,.45); }
        50% { opacity: .75; box-shadow: 0 0 0 5px rgba(102,209,112,0); }
      }
      @keyframes orellaSpin { to { transform: rotate(360deg); } }
    `}</style>
  )
}

/**
 * useMicLevel — real amplitude off an AnalyserNode on the SAME MediaStream the
 * recorder is using. Returns a 0..1 level, and 0 whenever nothing is attached.
 *
 * Fail-soft by construction: if AudioContext is unavailable or throws, the
 * level stays 0 and the meter renders its floor. A dead meter must never take
 * the recording down with it.
 *
 * The parent calls attach(stream) right after mediaRecorder.start() and
 * detach() on stop/pause-teardown/unmount.
 */
export function useMicLevel(logPrefix = 'VoiceRecorder') {
  const [level, setLevel] = useState(0)
  const ctxRef = useRef(null)
  const rafRef = useRef(null)

  const detach = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    if (ctxRef.current) {
      try { ctxRef.current.close() } catch { /* already closed */ }
      ctxRef.current = null
    }
    setLevel(0)
  }, [])

  const attach = useCallback((stream) => {
    if (!stream) return
    detach()
    try {
      const ACtx = window.AudioContext || window.webkitAudioContext
      if (!ACtx) return
      const actx = new ACtx()
      ctxRef.current = actx
      const src = actx.createMediaStreamSource(stream)
      const analyser = actx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.75
      src.connect(analyser)
      const buf = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        analyser.getByteFrequencyData(buf)
        let sum = 0
        for (let i = 0; i < buf.length; i++) sum += buf[i]
        // /90 matches the divisor DescribeVoice shipped and tuned against.
        setLevel(Math.min(1, (sum / buf.length) / 90))
        rafRef.current = requestAnimationFrame(tick)
      }
      tick()
    } catch (e) {
      console.warn(`[${logPrefix}] level meter unavailable:`, e?.message)
    }
  }, [detach, logPrefix])

  useEffect(() => detach, [detach])

  return { level, attach, detach }
}

// ─────────────────────────────────────────────────────────────────────────
// Label resolution — see the header note on failing open.
// ─────────────────────────────────────────────────────────────────────────
function useLabels(ns) {
  const { t } = useI18n()
  // i18n/index.jsx already implements `t(key, fallback)` with an English-locale
  // fallback in front of it. Writing a second resolver here would be exactly
  // the drift this module exists to remove — so use the one that ships.
  return useCallback((key, fallback) => t(`${ns}.${key}`, fallback), [t, ns])
}

/**
 * IDLE HERO — mic-first, "or type instead" as the secondary affordance.
 */
export function RecorderIdleHero({ ns, onRecord, onType, locked = false, lockedLabel }) {
  const ts = useLabels(ns)
  return (
    <div className="bg-gray-50 rounded-xl p-8 flex flex-col items-center">
      <button
        onClick={locked ? undefined : onRecord}
        disabled={locked}
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-all border-none ${
          locked ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#46A8F9] hover:bg-[#3A8FD6] active:scale-95 breathe-blue cursor-pointer'
        }`}
        style={{ boxShadow: locked ? 'none' : '0 8px 24px rgba(70,168,249,0.35)' }}
      >
        <Mic className="text-white" size={30} strokeWidth={1.8} />
      </button>
      <p className="text-sm font-semibold" style={{ color: NAVY }}>
        {locked ? (lockedLabel || 'Recording locked') : ts('tapToRecord', 'Tap to record')}
      </p>
      <p className="text-xs text-gray-400 mt-1">{ts('upTo3MinutesPause', 'Up to 3 minutes — you can pause')}</p>

      {!locked && onType && (
        <button
          onClick={onType}
          className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium bg-transparent border-none cursor-pointer"
          style={{ color: NAVY, borderBottom: '1.5px solid #15354F', paddingBottom: '3px', lineHeight: 1 }}
        >
          <Type size={12} /> {ts('orTypeInstead', 'or type instead')}
        </button>
      )}
    </div>
  )
}

/**
 * LIVE PANEL — recording / paused.
 *
 * Replaces the red countdown with a breathing green dot: recording is signalled
 * by the dot, and the timer is demoted to secondary. The five bars are driven
 * by `level`, which is real amplitude — a bar that moves because the patient
 * spoke is the only honest confirmation that the mic is live, and it is what
 * the 36 random-height bars could never give.
 */
export function RecorderLivePanel({
  ns, state, seconds, level = 0, heardSpeech = false, onPause, onResume, onStop,
}) {
  const ts = useLabels(ns)
  const recording = state === 'recording'

  return (
    <div className="rounded-2xl p-5" style={{ background: '#FBFCFD', border: '1px solid #E8EDF2' }}>
      {/* Status row */}
      <div className="flex items-center justify-center gap-2.5 mb-3.5">
        <span
          className="rounded-full"
          style={{
            width: 8, height: 8,
            background: recording ? '#66D170' : '#A7D9FC',
            animation: recording ? 'orellaBreatheGreen 2.4s ease-in-out infinite' : 'none',
          }}
        />
        <span className="text-sm font-semibold" style={{ color: recording ? NAVY : '#64748B' }}>
          {recording ? ts('listening', 'Listening') : ts('paused', 'Paused')}
        </span>
        <span
          className="text-sm font-semibold tabular-nums pl-2.5"
          style={{ color: '#64748B', borderLeft: '1px solid #E8EDF2' }}
        >
          {formatRecordingTime(seconds)}
        </span>
      </div>

      {/* Five bars, real amplitude from the AnalyserNode. */}
      <div className="flex items-end justify-center gap-1 mb-3.5" style={{ height: 26 }}>
        {[0.55, 0.8, 1, 0.75, 0.5].map((w, i) => {
          const h = recording ? Math.max(4, Math.min(26, 4 + level * 22 * w)) : 4
          return (
            <span key={i} className="rounded-full"
              style={{
                width: 4, height: h,
                background: recording && level > 0.05 ? BLUE : '#A7D9FC',
                transition: 'height .12s ease, background .12s ease',
              }} />
          )
        })}
      </div>

      <p className="text-center text-sm pb-1" style={{ color: '#64748B' }}>
        {!recording
          ? ts('pausedHint', 'Tap resume when you are ready')
          : heardSpeech
            ? ts('hearingYou', 'I can hear you')
            : ts('listeningHint', 'Go ahead — take your time')}
      </p>

      <div className="flex items-center justify-center gap-3">
        {recording ? (
          <button onClick={onPause}
            className="px-5 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-semibold text-gray-600 flex items-center gap-1.5 cursor-pointer">
            <Pause size={14} /> {ts('pause', 'Pause')}
          </button>
        ) : (
          <button onClick={onResume}
            className="px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-1.5 border cursor-pointer"
            style={{ backgroundColor: ICE_BLUE, color: BLUE, borderColor: BLUE }}>
            <Mic size={14} /> {ts('resume', 'Resume')}
          </button>
        )}
        <button onClick={onStop}
          className="px-5 py-2.5 text-white rounded-full text-sm font-semibold flex items-center gap-1.5 border-none cursor-pointer"
          style={{ backgroundColor: NAVY, boxShadow: '0 2px 8px rgba(21,53,79,0.3)' }}>
          <Square size={12} /> {ts('done', 'Done')}
        </button>
      </div>
    </div>
  )
}

/**
 * TIDYING PANEL — what the streaming path shows after Done.
 *
 * On the streaming path the text has ALREADY arrived; nothing uploads and
 * nothing is transcribed after the fact. Showing the three-step Whisper
 * progress here would be theatre for work that is not happening, which is the
 * single most visible piece of "old whisper UX" the follow-up screen carried.
 */
export function TidyingPanel({ ns }) {
  const ts = useLabels(ns)
  return (
    <div className="rounded-2xl p-5 text-center" style={{ background: '#FBFCFD', border: '1px solid #E8EDF2' }}>
      <div className="mx-auto mb-3" style={{
        width: 34, height: 34, borderRadius: '50%',
        border: '2.5px solid ' + ICE_BLUE, borderTopColor: BLUE,
        animation: 'orellaSpin .9s linear infinite',
      }} />
      <p className="text-sm font-semibold" style={{ color: NAVY }}>{ts('tidyingUp', 'Tidying up your words')}</p>
      <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{ts('justAMoment', 'Just a moment')}</p>
    </div>
  )
}

// TranscribeProgress was REMOVED (WHISPER-REMOVAL-1). It animated a staged
// progress bar — uploading, transcribing, done — for the batch round trip.
// Streaming produces text live, so there is no round trip to narrate, and its
// two call sites went with the panels that held them.
